import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Sparkles, ChevronDown, ChevronUp, RefreshCw, Database,
  Trash2, ShieldCheck, Clock, CheckCircle2, XCircle, AlertTriangle,
  Camera, Activity, Copy, Check,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Data Hygiene Stats — v2.9.130
 * ──────────────────────────────
 * Compact one-line summary that mirrors the format of:
 *   • Pipeline Health
 *   • Listener Stats
 *   • Listener Signal Quality
 *
 * Header shows live aggregate stats (write-time cleaner + last
 * nightly job + lifetime canonical_songs merged). Click to expand
 * a detailed breakdown of every nightly step + write-time counters.
 *
 * Replaces the old `MaintenanceHealthStrip` (multi-row job grid)
 * with a more operator-friendly summary that surfaces actual
 * cleanup impact instead of just OK/STALE statuses.
 */

// ── Friendly Greek labels for nightly pipeline steps ───────────────
const STEP_LABELS = {
  cache_normalized_key_backfill: 'Shazam cache: συμπλήρωση normalized_key',
  canonical_key_backfill:        'Stream songs: συμπλήρωση canonical_key',
  shazam_adoption:               'Shazam adoption (Apple Music spelling)',
  artist_title_consolidation:    'Artist/Title consolidation (cache-trusted)',
  massfill:                      'Cover-art + ISRC mass-fill',
  itunes_direct_backfill:        'iTunes direct lookup',
  duplicate_cleanup_loop:        'Duplicate plays cleanup',
  canonical_songs_dedup:         'Canonical_songs dedup (Compare Stations)',
  chart_cache_invalidate:        'Chart cache invalidate',
};

const formatTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('el-GR', {
      hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit',
      timeZone: 'Europe/Athens', hour12: false,
    });
  } catch {
    return '—';
  }
};

const formatAge = (iso) => {
  if (!iso) return '—';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const hours = ms / (1000 * 60 * 60);
    if (hours < 1) return `πριν ${Math.round(hours * 60)}m`;
    if (hours < 24) return `πριν ${hours.toFixed(1)}h`;
    return `πριν ${Math.floor(hours / 24)}d`;
  } catch {
    return '—';
  }
};

const num = (v) => (v == null ? '—' : Number(v).toLocaleString('el-GR'));

// Render the "details" object inside an expanded step as key: value chips
const StepDetailChips = ({ details }) => {
  if (!details || typeof details !== 'object') return null;
  const entries = Object.entries(details).filter(([, v]) => v !== null && v !== undefined);
  if (!entries.length) return <span className="text-gray-500 text-[10px] italic">(χωρίς λεπτομέρειες)</span>;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {entries.map(([k, v]) => (
        <span
          key={k}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700/50 text-[10px] text-gray-700 dark:text-gray-300 font-mono"
          title={k}
        >
          <span className="text-gray-500 dark:text-gray-400">{k}:</span>
          <span className="font-semibold">
            {typeof v === 'object' ? JSON.stringify(v) : String(v)}
          </span>
        </span>
      ))}
    </div>
  );
};

const StepRow = ({ step }) => {
  const ok = !step.error;
  const Icon = ok ? CheckCircle2 : XCircle;
  return (
    <div
      className="border-l-2 border-purple-400/40 pl-3 py-1.5"
      data-testid={`hygiene-step-${step.step}`}
    >
      <div className="flex items-center gap-2 text-xs">
        <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${ok ? 'text-emerald-500' : 'text-red-500'}`} />
        <span className="font-medium text-gray-800 dark:text-gray-200 flex-1 truncate">
          {STEP_LABELS[step.step] || step.step}
        </span>
        <span className="font-mono text-[10px] text-gray-500 dark:text-gray-400 tabular-nums">
          {step.duration_s != null ? `${step.duration_s.toFixed ? step.duration_s.toFixed(2) : step.duration_s}s` : '—'}
        </span>
      </div>
      {step.error && (
        <div className="text-[11px] text-red-500 mt-0.5">⚠ {step.error}</div>
      )}
      <StepDetailChips details={step.details} />
    </div>
  );
};

const DataHygieneStats = ({ token }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(
        `${API}/api/admin/diagnostic/data-hygiene-stats`,
        { headers },
      );
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  }, [token]);

  const handleRefresh = useCallback(async (e) => {
    if (e) e.stopPropagation();
    setRefreshing(true);
    try {
      await fetchStats();
    } finally {
      setRefreshing(false);
    }
  }, [fetchStats]);

  const [indexCreating, setIndexCreating] = useState(false);
  const [indexResult, setIndexResult] = useState(null);
  const handleCreateIndex = useCallback(async () => {
    setIndexCreating(true);
    setIndexResult(null);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(
        `${API}/api/admin/diagnostic/ensure-itunes-direct-index`,
        null,
        { headers },
      );
      setIndexResult(res.data);
    } catch (err) {
      setIndexResult({ ok: false, error: err.response?.data?.detail || err.message });
    } finally {
      setIndexCreating(false);
    }
  }, [token]);

  // v2.9.134 — One-click "force nightly pipeline run" button.
  // Replaces the deprecated `MaintenanceHealthStrip` admin flow and
  // saves the operator from having to hit the endpoint via curl/postman.
  const [pipelineRunning, setPipelineRunning] = useState(false);
  const [pipelineResult, setPipelineResult] = useState(null);
  // v2.9.145 (2026-06-08) — "Force Hygiene Snapshot" button. Fires
  // ONLY the daily snapshot capture (≤2s) so the widget unblocks
  // immediately when the previous night's full pipeline stalled on
  // some upstream step. Operationally distinct from "Εκτέλεση Τώρα"
  // (which runs all 9 steps and takes 2-15 min).
  const [snapshotRunning, setSnapshotRunning] = useState(false);
  const [snapshotResult, setSnapshotResult] = useState(null);

  // v2.9.151 — Sanitizer Audit button. One-click hit on
  // /hygiene-snapshots-recent so the operator can verify whether the
  // write-time sanitizer has been producing non-zero counters over
  // the last N days. Result + JSON copy live below the controls so
  // everything stays in one place (no need to open a separate tab).
  const [auditRunning, setAuditRunning] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [auditJsonCopied, setAuditJsonCopied] = useState(false);
  const [auditDays, setAuditDays] = useState(14);

  const handleRunSanitizerAudit = useCallback(async (e) => {
    if (e) e.stopPropagation();
    setAuditRunning(true);
    setAuditResult(null);
    setAuditJsonCopied(false);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(
        `${API}/api/admin/diagnostic/hygiene-snapshots-recent`,
        { headers, params: { days: auditDays }, timeout: 30000 },
      );
      setAuditResult(res.data);
    } catch (err) {
      setAuditResult({
        _diagnostic_ok: false,
        error: err.response?.data?.detail || err.message,
      });
    } finally {
      setAuditRunning(false);
    }
  }, [token, auditDays]);

  const handleCopyAuditJson = useCallback(async (e) => {
    if (e) e.stopPropagation();
    if (!auditResult) return;
    try {
      const txt = JSON.stringify(auditResult, null, 2);
      // Try modern clipboard API first, fall back to textarea
      // (some browsers block clipboard in non-https iframe).
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(txt);
      } else {
        const ta = document.createElement('textarea');
        ta.value = txt;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setAuditJsonCopied(true);
      setTimeout(() => setAuditJsonCopied(false), 2000);
    } catch (err) {
      // silent — user can right-click → copy from the visible JSON
    }
  }, [auditResult]);

  const handleRunPipeline = useCallback(async (e) => {
    if (e) e.stopPropagation();
    setPipelineRunning(true);
    setPipelineResult(null);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(
        `${API}/api/admin/backfill/nightly-cleanup/run-now`,
        null,
        { headers, timeout: 10000 },
      );
      if (res.data?.status === 'already_running') {
        setPipelineResult({ ok: false, busy: true, error: 'Η διαδικασία τρέχει ήδη (προγραμματισμένη ή άλλη εκτέλεση)' });
        return;
      }
      setPipelineResult({
        ok: true,
        status: res.data?.status || 'started',
      });
      // Auto-refresh the stats every 30s for the next 4 minutes so
      // the operator sees the new run result land in the UI.
      let polls = 0;
      const poll = setInterval(async () => {
        polls += 1;
        await fetchStats();
        if (polls >= 8) clearInterval(poll);
      }, 30000);
    } catch (err) {
      setPipelineResult({
        ok: false,
        error: err.response?.data?.detail || err.message,
      });
    } finally {
      setPipelineRunning(false);
    }
  }, [token, fetchStats]);

  const handleForceSnapshot = useCallback(async (e) => {
    if (e) e.stopPropagation();
    setSnapshotRunning(true);
    setSnapshotResult(null);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(
        `${API}/api/admin/backfill/hygiene-snapshot/force`,
        null,
        { headers, timeout: 70000 },
      );
      const ok = res.data?.status === 'ok';
      setSnapshotResult({
        ok,
        status: res.data?.status || 'unknown',
        error: ok ? null : (res.data?.error || res.data?.message || 'unknown error'),
      });
      // Pull fresh stats so the new sparkline row shows up.
      await fetchStats();
    } catch (err) {
      setSnapshotResult({
        ok: false,
        error: err.response?.data?.detail || err.message,
      });
    } finally {
      setSnapshotRunning(false);
    }
  }, [token, fetchStats]);

  useEffect(() => {
    let mounted = true;
    // Defer initial fetch to next microtask so the effect itself
    // doesn't synchronously trigger state updates (react-hooks/
    // set-state-in-effect rule).
    Promise.resolve().then(() => {
      if (mounted) fetchStats();
    });
    const iv = setInterval(() => {
      if (mounted) fetchStats();
    }, 5 * 60 * 1000);
    return () => {
      mounted = false;
      clearInterval(iv);
    };
  }, [fetchStats]);

  // v2.9.139 — When a pipeline is actively running, accelerate the
  // refresh cadence to every 3s so the progress bar feels responsive.
  // Must run BEFORE any conditional returns (rules-of-hooks).
  const isRunning = data?.running_progress?.is_running;
  useEffect(() => {
    if (!isRunning) return;
    const iv = setInterval(() => fetchStats(), 3000);
    return () => clearInterval(iv);
  }, [isRunning, fetchStats]);

  if (error) {
    return (
      <div
        className="text-xs px-3 py-2 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800"
        data-testid="data-hygiene-stats-error"
      >
        Data Hygiene: σφάλμα φόρτωσης ({error})
      </div>
    );
  }

  if (!data) {
    return (
      <div
        className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 animate-pulse"
        data-testid="data-hygiene-stats-loading"
      >
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48" />
      </div>
    );
  }

  const wt = data.write_time || {};
  const ln = data.last_nightly || {};
  const lt = data.lifetime || {};
  const s7 = data.series_7d || [];
  const rp = data.running_progress;  // v2.9.139 — live progress

  // Build 7-day series for each metric. Frontend extracts {v} arrays
  // keyed by the metric name. Date is included for tooltip support
  // later if needed.
  const buildSeries = (key) =>
    s7.map((d) => ({ date: d.date, v: Number(d[key] || 0) }));

  const cleaned = (wt.titles_cleaned || 0) + (wt.artists_cleaned || 0);
  const dropped = (wt.plays_dropped_junk_title || 0) + (wt.plays_dropped_junk_artist || 0);
  const nightlyOk = ln.status === 'ok';
  const hasIssue = !nightlyOk || ln.error;

  return (
    <div
      className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
      data-testid="data-hygiene-stats"
    >
      {/* ─── Summary Bar (clickable area for expand, with inline buttons) ── */}
      <div
        className={`w-full px-4 py-2.5 transition-colors ${hasIssue ? 'bg-amber-500/5' : ''}`}
        data-testid="data-hygiene-stats-container"
      >
        <div className="flex items-center justify-between">
          {/* Left: clickable expand region (everything but the action buttons) */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => setExpanded(!expanded)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded(!expanded); }}
            className="flex-1 flex items-center gap-4 text-sm flex-wrap cursor-pointer hover:opacity-90"
            data-testid="data-hygiene-stats-toggle"
          >
          <div className="flex items-center gap-1.5 font-medium text-gray-700 dark:text-gray-300">
            <Sparkles className="w-4 h-4 text-purple-500" />
            <span>Data Hygiene Stats</span>
            {hasIssue && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-500 text-[10px] font-bold ml-1"
                title="Τελευταίο nightly run απέτυχε ή χρειάζεται προσοχή"
                data-testid="data-hygiene-issue-badge"
              >
                <AlertTriangle className="w-3 h-3" />
                προσοχή
              </span>
            )}
          </div>

          {/* Three compact KPI groups */}
          <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
            {/* Write-time */}
            <span
              className="inline-flex items-center gap-1"
              title="Write-time καθαρισμός: ICY metadata καθαρισμένα + plays που drop-αρίστηκαν ως junk πριν φτάσουν στη βάση"
              data-testid="hygiene-summary-write"
            >
              <Trash2 className="w-3.5 h-3.5 text-amber-500" />
              <span className="font-mono tabular-nums">
                <strong className="text-gray-800 dark:text-gray-200">{num(cleaned)}</strong>
                <span className="text-gray-500"> καθαρ.</span>
                {dropped > 0 && (
                  <>
                    <span className="text-gray-500"> · </span>
                    <strong className="text-amber-600 dark:text-amber-400">{num(dropped)}</strong>
                    <span className="text-gray-500"> drop</span>
                  </>
                )}
              </span>
            </span>

            {/* Last nightly */}
            <span
              className="inline-flex items-center gap-1"
              title="Τελευταίο νυχτερινό job συντήρησης"
              data-testid="hygiene-summary-nightly"
            >
              <Clock className="w-3.5 h-3.5 text-sky-500" />
              <span>
                <strong className={`${nightlyOk ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
                  {nightlyOk ? 'OK' : (ln.status || 'never')}
                </strong>
                <span className="text-gray-500"> · {formatAge(ln.finished_at)}</span>
              </span>
            </span>

            {/* Canonical_songs merged total */}
            <span
              className="inline-flex items-center gap-1"
              title="Διπλότυπα canonical_songs που έχουν ενοποιηθεί συνολικά (επηρεάζει το Compare Stations)"
              data-testid="hygiene-summary-merged"
            >
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
              <span className="font-mono tabular-nums">
                <strong className="text-gray-800 dark:text-gray-200">{num(lt.canonical_songs_merged)}</strong>
                <span className="text-gray-500"> dedup</span>
              </span>
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* v2.9.134 — One-click force-run nightly pipeline button.
              Visible in the header so it's reachable WITHOUT expanding
              the panel. Replaces the curl/postman workflow. */}
          <button
            onClick={handleRunPipeline}
            disabled={pipelineRunning}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium border transition-colors disabled:opacity-50 disabled:cursor-wait ${
              pipelineResult?.ok
                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
                : pipelineResult?.ok === false
                ? 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30'
                : 'bg-purple-500/10 hover:bg-purple-500/20 text-purple-600 dark:text-purple-400 border-purple-500/30'
            }`}
            title={
              pipelineResult?.ok
                ? 'Pipeline ξεκίνησε ✓ — Τα αποτελέσματα θα φανούν εδώ σε ~2-4 λεπτά'
                : pipelineResult?.ok === false
                ? `Σφάλμα: ${pipelineResult.error}`
                : 'Άμεση εκτέλεση ολόκληρου του nightly pipeline (Shazam adoption, consolidation, dedup, hygiene snapshot)'
            }
            data-testid="run-nightly-pipeline-btn"
          >
            {pipelineRunning
              ? <RefreshCw className="w-3 h-3 animate-spin" />
              : pipelineResult?.ok
              ? <CheckCircle2 className="w-3 h-3" />
              : pipelineResult?.ok === false
              ? <XCircle className="w-3 h-3" />
              : <Sparkles className="w-3 h-3" />}
            {pipelineRunning
              ? 'Εκτέλεση...'
              : pipelineResult?.ok
              ? 'Ξεκίνησε'
              : pipelineResult?.busy
              ? 'Τρέχει ήδη'
              : pipelineResult?.ok === false
              ? 'Σφάλμα'
              : 'Εκτέλεση Τώρα'}
          </button>

          {/* v2.9.145 (2026-06-08) — "Force Snapshot" tiny button.
              Fires ONLY the daily hygiene snapshot (~2s) so the
              widget unblocks even when the full nightly pipeline
              stalled on some upstream step. Distinct intent from
              "Εκτέλεση Τώρα" — that one runs all 9 steps. */}
          <button
            onClick={handleForceSnapshot}
            disabled={snapshotRunning}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium border transition-colors disabled:opacity-50 disabled:cursor-wait ${
              snapshotResult?.ok
                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
                : snapshotResult?.ok === false
                ? 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30'
                : 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/30'
            }`}
            title={
              snapshotResult?.ok
                ? 'Snapshot ολοκληρώθηκε ✓ — Το widget ανανεώθηκε.'
                : snapshotResult?.ok === false
                ? `Σφάλμα: ${snapshotResult.error}`
                : 'Άμεσο unblock: εκτελεί ΜΟΝΟ το daily snapshot (~2s). Χρήσιμο όταν το προηγούμενο nightly κόλλησε.'
            }
            data-testid="force-hygiene-snapshot-btn"
          >
            {snapshotRunning
              ? <RefreshCw className="w-3 h-3 animate-spin" />
              : snapshotResult?.ok
              ? <CheckCircle2 className="w-3 h-3" />
              : snapshotResult?.ok === false
              ? <XCircle className="w-3 h-3" />
              : <Camera className="w-3 h-3" />}
            {snapshotRunning
              ? '...'
              : snapshotResult?.ok
              ? 'OK'
              : snapshotResult?.ok === false
              ? 'Σφάλμα'
              : 'Force Snapshot'}
          </button>

          {/* v2.9.151 — Sanitizer Audit button.
              Calls /hygiene-snapshots-recent and surfaces the
              verdict ("sanitizer_running_regularly" /
              "sanitizer_inactive" / etc.) so the operator can
              verify the write-time sanitizer pipeline without
              opening mongosh / Atlas Compass. The full JSON is
              copyable with the adjacent Copy JSON button. */}
          <button
            onClick={handleRunSanitizerAudit}
            disabled={auditRunning}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium border transition-colors disabled:opacity-50 disabled:cursor-wait ${
              auditResult?.verdict === 'sanitizer_running_regularly'
                || auditResult?.verdict === 'sanitizer_running_occasionally'
                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
                : auditResult?.verdict === 'sanitizer_inactive'
                ? 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30'
                : auditResult?.verdict
                ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30'
                : 'bg-sky-500/10 hover:bg-sky-500/20 text-sky-600 dark:text-sky-400 border-sky-500/30'
            }`}
            title={
              auditResult?.verdict_explanation
                || 'Έλεγχος αν τρέχει ο write-time sanitizer — τραβάει τα τελευταία N ημερήσια snapshots από Mongo'
            }
            data-testid="sanitizer-audit-btn"
          >
            {auditRunning
              ? <RefreshCw className="w-3 h-3 animate-spin" />
              : auditResult?.verdict === 'sanitizer_running_regularly'
                || auditResult?.verdict === 'sanitizer_running_occasionally'
              ? <CheckCircle2 className="w-3 h-3" />
              : auditResult?.verdict === 'sanitizer_inactive'
              ? <XCircle className="w-3 h-3" />
              : <Activity className="w-3 h-3" />}
            {auditRunning ? '...' : 'Έλεγχος Sanitizer'}
          </button>

          {/* Copy JSON button — appears only after an audit ran */}
          {auditResult && (
            <button
              onClick={handleCopyAuditJson}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium border bg-purple-500/10 hover:bg-purple-500/20 text-purple-600 dark:text-purple-400 border-purple-500/30 transition-colors"
              title="Αντιγραφή πλήρους JSON στο clipboard"
              data-testid="sanitizer-audit-copy-json-btn"
            >
              {auditJsonCopied
                ? <Check className="w-3 h-3" />
                : <Copy className="w-3 h-3" />}
              {auditJsonCopied ? 'Αντιγράφηκε!' : 'Copy JSON'}
            </button>
          )}

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
            title="Ανανέωση"
            data-testid="data-hygiene-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-gray-500 dark:text-gray-400 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          {expanded
            ? <ChevronUp className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            : <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />}
        </div>
        </div>
      </div>

      {/* v2.9.151 — Sanitizer Audit Result Panel
          Renders only after the operator has clicked "Έλεγχος Sanitizer".
          Surfaces the verdict + per-day summary + copy-able JSON
          without forcing them to switch to another tab. */}
      {auditResult && (
        <div
          className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-4 py-3"
          data-testid="sanitizer-audit-panel"
        >
          {auditResult._diagnostic_ok === false ? (
            <div className="flex items-start gap-2 text-xs text-red-600 dark:text-red-400">
              <XCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Σφάλμα ελέγχου sanitizer</div>
                <div className="text-[11px] mt-0.5 font-mono">{auditResult.error}</div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold text-gray-700 dark:text-gray-300">
                    Verdict:
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                      auditResult.verdict?.includes('running')
                        ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                        : auditResult.verdict === 'sanitizer_inactive'
                        ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                        : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                    }`}
                    data-testid="sanitizer-audit-verdict"
                  >
                    {auditResult.verdict}
                  </span>
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">
                    ({auditResult.returned_count} ημέρες δεδομένα)
                  </span>
                </div>
                <label className="ml-auto flex items-center gap-2 text-[11px] text-gray-600 dark:text-gray-400">
                  Window:
                  <select
                    value={auditDays}
                    onChange={(e) => setAuditDays(parseInt(e.target.value, 10))}
                    className="px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-xs"
                    data-testid="sanitizer-audit-days-select"
                  >
                    {[7, 14, 30, 60, 90].map((d) => (
                      <option key={d} value={d}>{d}d</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="text-[11px] text-gray-600 dark:text-gray-400 mb-2 italic">
                {auditResult.verdict_explanation}
              </div>

              {/* Per-day summary table — compact, scrollable */}
              {auditResult.snapshots && auditResult.snapshots.length > 0 && (
                <div className="max-h-48 overflow-y-auto rounded border border-gray-200 dark:border-gray-700">
                  <table className="w-full text-[11px]">
                    <thead className="bg-gray-100 dark:bg-gray-800 sticky top-0">
                      <tr className="text-left text-gray-700 dark:text-gray-300">
                        <th className="px-2 py-1 font-semibold">Ημέρα</th>
                        <th className="px-2 py-1 font-semibold text-right">Titles</th>
                        <th className="px-2 py-1 font-semibold text-right">Artists</th>
                        <th className="px-2 py-1 font-semibold text-right">Drops</th>
                        <th className="px-2 py-1 font-semibold text-right">Renames</th>
                        <th className="px-2 py-1 font-semibold text-right">Dedup</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {auditResult.snapshots.map((s) => {
                        const sm = s.summary || {};
                        const hasActivity = (
                          sm.titles_cleaned + sm.artists_cleaned
                          + sm.plays_dropped_junk_title + sm.plays_dropped_junk_artist
                        ) > 0;
                        return (
                          <tr
                            key={s.date_key}
                            className={hasActivity ? '' : 'opacity-60'}
                          >
                            <td className="px-2 py-1 font-mono text-gray-700 dark:text-gray-300">
                              {s.date_key}
                            </td>
                            <td className="px-2 py-1 text-right font-mono">
                              {sm.titles_cleaned}
                            </td>
                            <td className="px-2 py-1 text-right font-mono">
                              {sm.artists_cleaned}
                            </td>
                            <td className="px-2 py-1 text-right font-mono">
                              {sm.plays_dropped_junk_title + sm.plays_dropped_junk_artist}
                            </td>
                            <td className="px-2 py-1 text-right font-mono">
                              {sm.shazam_renames + sm.consolidation_renames}
                            </td>
                            <td className="px-2 py-1 text-right font-mono">
                              {sm.canonicals_merged}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {auditResult.snapshots && auditResult.snapshots.length === 0 && (
                <div className="text-[11px] text-gray-500 dark:text-gray-400 italic py-2">
                  Δεν υπάρχουν snapshots στο επιλεγμένο window.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* v2.9.139 — LIVE PROGRESS BAR (shown only while running) */}
      {rp?.is_running && (
        <div
          className="border-t border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-50 via-purple-100/50 to-purple-50 dark:from-purple-900/20 dark:via-purple-800/15 dark:to-purple-900/20 px-4 py-2.5"
          data-testid="hygiene-progress-bar"
        >
          <div className="flex items-center justify-between gap-3 text-xs mb-1.5">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <RefreshCw className="w-3.5 h-3.5 animate-spin text-purple-500 flex-shrink-0" />
              <span className="font-semibold text-purple-700 dark:text-purple-300 truncate">
                {rp.current_step || 'Pipeline ξεκινάει...'}
              </span>
              {rp.step_idx != null && rp.total_steps != null && (
                <span className="text-[10px] text-gray-600 dark:text-gray-400 font-mono whitespace-nowrap">
                  ({rp.step_idx}/{rp.total_steps})
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-[10px] text-gray-600 dark:text-gray-400 font-mono tabular-nums whitespace-nowrap">
              <Clock className="w-3 h-3" />
              <span data-testid="hygiene-progress-elapsed">
                {rp.elapsed_seconds != null
                  ? `${Math.floor(rp.elapsed_seconds / 60)}:${String(Math.floor(rp.elapsed_seconds % 60)).padStart(2, '0')}`
                  : '0:00'}
              </span>
              {rp.percent != null && (
                <span className="text-purple-600 dark:text-purple-400 font-bold">
                  {rp.percent.toFixed(0)}%
                </span>
              )}
            </div>
          </div>
          {/* Progress fill */}
          <div className="w-full h-1.5 bg-purple-200/40 dark:bg-purple-900/40 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-purple-400 transition-all duration-500 ease-out"
              style={{ width: `${Math.max(2, rp.percent || 0)}%` }}
            />
          </div>
        </div>
      )}

      {/* ─── Expanded body ────────────────────────────────────── */}
      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/30 px-4 py-3 space-y-4">
          {/* Section 1: Write-time counters */}
          <section data-testid="hygiene-section-write">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
              <Trash2 className="w-3.5 h-3.5 text-amber-500" />
              <span>Καθαρισμός Write-Time (από τελευταίο restart, {wt.uptime_hours ?? '—'}h uptime)</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <KPIBlock
                label="Titles καθαρισμένα"
                value={wt.titles_cleaned}
                series={buildSeries('titles_cleaned')}
                hint="Παρενθέσεις & promo markers αφαιρέθηκαν από τίτλο τραγουδιού πριν την αποθήκευση"
              />
              <KPIBlock
                label="Artists καθαρισμένα"
                value={wt.artists_cleaned}
                series={buildSeries('artists_cleaned')}
                hint="Promo markers αφαιρέθηκαν από όνομα καλλιτέχνη πριν την αποθήκευση"
              />
              <KPIBlock
                label="Drops: junk title"
                value={wt.plays_dropped_junk_title}
                series={buildSeries('plays_dropped_junk_title')}
                tone="warn"
                hint="Plays που drop-αρίστηκαν επειδή ο τίτλος ήταν junk (κανάλι N, platform IDs, frequency markers). Η γραμμή προς τα κάτω σημαίνει καλύτερη υγεία metadata."
              />
              <KPIBlock
                label="Drops: junk artist"
                value={wt.plays_dropped_junk_artist}
                series={buildSeries('plays_dropped_junk_artist')}
                tone="warn"
                hint="Plays που drop-αρίστηκαν επειδή ο καλλιτέχνης ήταν junk (Unknown Artist, Ineer Circle). Η γραμμή προς τα κάτω σημαίνει καλύτερη υγεία metadata."
              />
            </div>
          </section>

          {/* Section 2: Last nightly pipeline */}
          <section data-testid="hygiene-section-nightly">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-300">
                <Clock className="w-3.5 h-3.5 text-sky-500" />
                <span>Τελευταίο Nightly Run</span>
                {ln.finished_at && (
                  <span className="text-[10px] text-gray-500 dark:text-gray-400 font-normal">
                    ({formatTime(ln.finished_at)} · {ln.total_duration_s?.toFixed?.(1) || ln.total_duration_s}s · {formatAge(ln.finished_at)})
                  </span>
                )}
              </div>

              {/* v2.9.133 — Inline action: create the iTunes index if a
                  timeout was detected in the latest run. Shows only when
                  there's actually a problem to fix. */}
              {(() => {
                const itunesStep = (ln.steps || []).find(
                  (s) => s.step === 'itunes_direct_backfill',
                );
                const hadTimeout = itunesStep?.details?.aggregate_timed_out
                  || (itunesStep?.error || '').toLowerCase().includes('timeout');
                if (!hadTimeout) return null;
                return (
                  <div className="flex items-center gap-2">
                    {indexResult && (
                      <span
                        className={`text-[10px] ${indexResult.ok ? 'text-emerald-500' : 'text-red-500'}`}
                        data-testid="itunes-index-result"
                      >
                        {indexResult.ok
                          ? (indexResult.existed
                              ? 'Index already exists ✓'
                              : 'Index created ✓')
                          : `⚠ ${indexResult.error}`}
                      </span>
                    )}
                    <button
                      onClick={handleCreateIndex}
                      disabled={indexCreating}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/30 transition-colors disabled:opacity-50 disabled:cursor-wait"
                      title="Δημιουργία compound index για iTunes step ώστε να αποφύγει Atlas read timeouts"
                      data-testid="create-itunes-index-btn"
                    >
                      {indexCreating
                        ? <RefreshCw className="w-3 h-3 animate-spin" />
                        : <Database className="w-3 h-3" />}
                      Δημιουργία iTunes Index
                    </button>
                  </div>
                );
              })()}
            </div>
            {ln.error ? (
              <div className="text-xs text-red-500">⚠ {ln.error}</div>
            ) : (
              <div className="space-y-0.5">
                {(ln.steps || []).map((step, idx) => (
                  <StepRow key={`${step.step}-${idx}`} step={step} />
                ))}
              </div>
            )}
            <div className="mt-2 text-[10px] text-gray-500 dark:text-gray-400">
              Παράθυρο: 01:00–04:30 Europe/Athens · auto-scheduled
            </div>
          </section>

          {/* Section 3: Lifetime database health */}
          <section data-testid="hygiene-section-lifetime">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
              <Database className="w-3.5 h-3.5 text-purple-500" />
              <span>Lifetime Database Health</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
              <KPIBlock
                label="Canonical_songs active"
                value={lt.canonical_songs_active}
                hint="Μοναδικά τραγούδια που χρησιμοποιεί το Compare Stations"
              />
              <KPIBlock
                label="Dedup merged (lifetime)"
                value={lt.canonical_songs_merged}
                series={buildSeries('canonicals_merged')}
                tone="good"
                hint="Διπλότυπα canonical_songs που έχουν ενοποιηθεί. Sparkline = νέα ενοποιήσεις ανά μέρα — προς τα κάτω = η βάση συγκλίνει σε καθαρή κατάσταση."
              />
              <KPIBlock
                label="Dedup audit entries"
                value={lt.dedup_audit_entries}
                hint="Κάθε merge καταγράφεται για rollback safety"
              />
              <KPIBlock
                label="Shazam cache size"
                value={lt.shazam_recognition_cache_size}
                hint="Apple Music canonical names cached"
              />
              <KPIBlock
                label="Cache με ISRC"
                value={lt.shazam_cache_with_isrc}
                tone="good"
                hint="Cache records με IFPI/ISO-3901 ISRC"
              />
            </div>
            {data.today_shazam_renames != null && (
              <div className="mt-2 text-[11px] text-gray-600 dark:text-gray-400">
                <span className="font-medium">Σήμερα:</span> {num(data.today_shazam_renames)} stream_songs υιοθέτησαν canonical spelling από Shazam cache.
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

// ── Inline SVG sparkline ─────────────────────────────────────────────
// Tiny path that maps a numeric series to a w×h area. Used as a
// secondary visual under each KPI to show 7-day trend at a glance.
// Color carries semantic meaning: green = trending down for "drops"
// (good), red = trending up for "drops" (bad). For positive metrics
// (titles_cleaned, etc.) the convention is inverted.
const Sparkline = ({ series, tone, height = 22, width = 76 }) => {
  if (!series || series.length < 2) {
    return (
      <div
        className="text-[9px] text-gray-500 dark:text-gray-600 italic"
        style={{ height }}
      >
        (περισσότερες μέρες έρχονται)
      </div>
    );
  }
  const values = series.map((s) => s.v ?? 0);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const step = width / Math.max(1, values.length - 1);
  const points = values.map((v, i) => {
    const x = i * step;
    const y = height - 4 - ((v - min) / range) * (height - 8);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  // Trend: last vs first non-zero value
  const first = values[0];
  const last = values[values.length - 1];
  const delta = last - first;
  // For "warn" metrics (drops) → up is bad, down is good
  // For default/good metrics → up is neutral/good
  let stroke;
  if (tone === 'warn') {
    stroke = delta > 0 ? '#ef4444' : delta < 0 ? '#10b981' : '#9ca3af';
  } else if (tone === 'good') {
    stroke = delta > 0 ? '#10b981' : delta < 0 ? '#f59e0b' : '#9ca3af';
  } else {
    stroke = '#a855f7'; // purple = neutral trend
  }
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="mt-1"
      data-testid="kpi-sparkline"
    >
      <polyline
        fill="none"
        stroke={stroke}
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points.join(' ')}
      />
      {/* Highlight the most recent point */}
      <circle
        cx={(values.length - 1) * step}
        cy={height - 4 - ((last - min) / range) * (height - 8)}
        r="1.8"
        fill={stroke}
      />
    </svg>
  );
};

// Small KPI block used inside the expanded sections (with optional sparkline)
const KPIBlock = ({ label, value, hint, tone, series }) => {
  const valCls = tone === 'warn'
    ? 'text-amber-600 dark:text-amber-400'
    : tone === 'good'
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-gray-800 dark:text-gray-200';
  return (
    <div
      className="bg-gray-50 dark:bg-gray-800/50 rounded border border-gray-200 dark:border-gray-700 px-2 py-1.5"
      title={hint}
    >
      <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{label}</div>
      <div className={`font-mono tabular-nums text-base font-bold ${valCls}`}>
        {num(value)}
      </div>
      {series !== undefined && (
        <Sparkline series={series} tone={tone} />
      )}
    </div>
  );
};

// Default export at end of file

export default DataHygieneStats;
